import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getNotificationPreferences, updateNotificationPreferences } from '../services/notifications'
import { queryClient } from '../lib/react-query'
import Modal from './Modal'

const NotificationPreferencesModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common')
  const [emailEnabled, setEmailEnabled] = useState(true)
  const [selectedTypes, setSelectedTypes] = useState([])
  const [hasChanges, setHasChanges] = useState(false)

  // Fetch current preferences
  const { data, isLoading, error } = useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: getNotificationPreferences,
    enabled: isOpen,
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] })
      setHasChanges(false)
      onClose()
    },
  })

  // Initialize state from fetched data
  useEffect(() => {
    if (data?.notificationPreferences) {
      setEmailEnabled(data.notificationPreferences.emailEnabled ?? true)
      setSelectedTypes(data.notificationPreferences.emailTypes || [])
    }
  }, [data])

  // Track changes
  useEffect(() => {
    if (data?.notificationPreferences) {
      const originalEnabled = data.notificationPreferences.emailEnabled ?? true
      const originalTypes = data.notificationPreferences.emailTypes || []

      const enabledChanged = emailEnabled !== originalEnabled
      const typesChanged = JSON.stringify([...selectedTypes].sort()) !== JSON.stringify([...originalTypes].sort())

      setHasChanges(enabledChanged || typesChanged)
    }
  }, [emailEnabled, selectedTypes, data])

  const handleTypeToggle = (type) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    )
  }

  const handleSelectAll = () => {
    if (data?.availableTypes) {
      setSelectedTypes(data.availableTypes.map(t => t.type))
    }
  }

  const handleSelectNone = () => {
    setSelectedTypes([])
  }

  const handleSave = () => {
    updateMutation.mutate({
      emailEnabled,
      emailTypes: selectedTypes,
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('notificationPrefs.title')} size="sm">
      {isLoading && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            {t('notificationPrefs.loading')}
          </div>
        )}

        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c00',
            marginBottom: '1rem'
          }}>
            {t('notificationPrefs.errorLoading', { message: error.message })}
          </div>
        )}

        {!isLoading && !error && (
          <>
            {/* Email Toggle */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={emailEnabled}
                  onChange={(e) => setEmailEnabled(e.target.checked)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    {t('notificationPrefs.enableEmail')}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    {t('notificationPrefs.enableEmailHelp')}
                  </div>
                </div>
              </label>
            </div>

            {/* Notification Types */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.75rem'
              }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                  {t('notificationPrefs.notificationTypes')}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={handleSelectAll}
                    disabled={!emailEnabled}
                    style={{
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.8rem',
                      backgroundColor: emailEnabled ? 'var(--color-border)' : '#f5f5f5',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      cursor: emailEnabled ? 'pointer' : 'not-allowed',
                      color: emailEnabled ? '#495057' : '#999'
                    }}
                  >
                    {t('notificationPrefs.selectAll')}
                  </button>
                  <button
                    onClick={handleSelectNone}
                    disabled={!emailEnabled}
                    style={{
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.8rem',
                      backgroundColor: emailEnabled ? 'var(--color-border)' : '#f5f5f5',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      cursor: emailEnabled ? 'pointer' : 'not-allowed',
                      color: emailEnabled ? '#495057' : '#999'
                    }}
                  >
                    {t('notificationPrefs.selectNone')}
                  </button>
                </div>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                opacity: emailEnabled ? 1 : 0.5
              }}>
                {data?.availableTypes?.map((typeInfo) => (
                  <label
                    key={typeInfo.type}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      backgroundColor: selectedTypes.includes(typeInfo.type) ? '#e8f4fd' : '#fff',
                      border: '1px solid',
                      borderColor: selectedTypes.includes(typeInfo.type) ? '#90caf9' : '#dee2e6',
                      borderRadius: '6px',
                      cursor: emailEnabled ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(typeInfo.type)}
                      onChange={() => handleTypeToggle(typeInfo.type)}
                      disabled={!emailEnabled}
                      style={{
                        width: '18px',
                        height: '18px',
                        marginTop: '2px',
                        cursor: emailEnabled ? 'pointer' : 'not-allowed'
                      }}
                    />
                    <div>
                      <div style={{
                        fontWeight: '500',
                        marginBottom: '0.25rem',
                        color: emailEnabled ? '#333' : '#999'
                      }}>
                        {t(`notificationPrefs.types.${typeInfo.type}.name`)}
                      </div>
                      <div style={{
                        fontSize: '0.85rem',
                        color: emailEnabled ? '#666' : '#999'
                      }}>
                        {t(`notificationPrefs.types.${typeInfo.type}.description`)}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Error Message */}
            {updateMutation.isError && (
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                color: '#c00',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                {t('notificationPrefs.errorSaving', { message: updateMutation.error?.message || t('labels.unknown') })}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end',
              paddingTop: '1rem',
              borderTop: '1px solid #eee'
            }}>
              <button
                onClick={onClose}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                {t('buttons.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || updateMutation.isPending}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: hasChanges ? 'var(--color-primary)' : 'var(--color-border)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: hasChanges && !updateMutation.isPending ? 'pointer' : 'not-allowed',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                {updateMutation.isPending ? t('notificationPrefs.saving') : t('notificationPrefs.saveButton')}
              </button>
            </div>
          </>
        )}
    </Modal>
  )
}

export default NotificationPreferencesModal
